import React, { useState, useEffect } from 'react';
import './GameMaker.css';

/**
 * Unified CollapsibleBox Component for GameMaker
 * 
 * Provides consistent collapsible UI pattern across offer templates, offers, and game instances.
 * Features:
 * - High-contrast styling with priority-based colors
 * - localStorage persistence for collapsed state
 * - Flexible content and header configuration
 * - Responsive design with proper accessibility
 * - Transaction feedback integration ready
 */
const CollapsibleBox = ({
  // Core props
  title,
  subtitle,
  children,
  
  // Styling and behavior
  priority = 'default', // 'high' | 'medium' | 'low' | 'default'
  backgroundColor,
  isCollapsed: externalIsCollapsed,
  onToggle,
  
  // State persistence
  storageKey,
  
  // Content props
  itemCount = 0,
  totalValue = '',
  status = '',
  
  // Transaction feedback
  isLoading = false,
  transactionFeedback,
  
  // Advanced options
  className = '',
  headerActions,
  showToggleIndicator = true,
  disabled = false,
  
  // Fast travel navigation
  sectionId = null
}) => {
  // Internal state management
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`collapsibleBox_${storageKey}`);
      return saved !== null ? JSON.parse(saved) : false;
    }
    return false;
  });

  // Use external state if provided, otherwise use internal state
  const isCollapsed = externalIsCollapsed !== undefined ? externalIsCollapsed : internalIsCollapsed;
  const setIsCollapsed = onToggle || setInternalIsCollapsed;

  // Save state to localStorage
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`collapsibleBox_${storageKey}`, JSON.stringify(isCollapsed));
    }
  }, [isCollapsed, storageKey]);

  // Map priority to CSS class names
  const getPriorityClassName = () => {
    const priorityMapping = {
      high: 'priority-high',
      medium: 'priority-medium', 
      low: 'priority-low',
      info: 'priority-info',
      purple: 'priority-purple',
      'red-light': 'priority-red-light',
      default: ''
    };
    return priorityMapping[priority] || '';
  };

  const priorityClassName = getPriorityClassName();

  // Handle toggle click
  const handleToggle = () => {
    if (disabled) return;
    
    if (onToggle) {
      onToggle(!isCollapsed);
    } else {
      setInternalIsCollapsed(!isCollapsed);
    }
  };

  // Build header content
  const renderHeader = () => (
    <div>
      <div className="text-lg font-bold">
        {title}
        {itemCount > 0 && (
          <span className="ml-2 px-2 py-1 bg-black text-white text-sm font-medium rounded-full">
            {itemCount}
          </span>
        )}
      </div>
      
      {subtitle && (
        <div className="text-sm mt-1 opacity-80">
          {subtitle}
        </div>
      )}

      {/* Status and value display */}
      <div className="flex items-center space-x-4 mt-2">
        {status && (
          <div className="text-sm font-medium">
            Status: {status}
          </div>
        )}
        {totalValue && (
          <div className="text-sm font-medium" style={{ whiteSpace: 'pre-wrap' }}>
            {totalValue}
          </div>
        )}
      </div>

      {/* Transaction feedback */}
      {transactionFeedback && (
        <div className={`transaction-feedback ${transactionFeedback.type}`}>
          <div className="flex items-center space-x-2">
            {transactionFeedback.type === 'pending' && (
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
            )}
            {transactionFeedback.type === 'success' && (
              <div className="h-4 w-4">✓</div>
            )}
            {transactionFeedback.type === 'error' && (
              <div className="h-4 w-4">✕</div>
            )}
            <div className="text-sm font-medium">
              {transactionFeedback.message}
            </div>
          </div>
        </div>
      )}

      {/* Header actions */}
      {headerActions && (
        <div className="flex items-center space-x-2 ml-4">
          {headerActions}
        </div>
      )}
    </div>
  );

  return (
    <div 
      className={`collapsible-box ${priorityClassName} ${isCollapsed ? 'collapsed' : 'expanded'} ${className} ${disabled ? 'opacity-60' : ''}`}
      style={backgroundColor ? { backgroundColor, borderColor: backgroundColor } : {}}
      data-section-id={sectionId}
    >
      {/* Clickable header */}
      <div 
        className={`collapsible-header ${disabled ? 'cursor-not-allowed' : ''}`}
        onClick={handleToggle}
      >
        {renderHeader()}
        
        {/* Toggle indicator */}
        {showToggleIndicator && (
          <div className="text-center mt-3 text-gray-500 text-sm select-none">
            {isCollapsed ? (
              <span className="inline-flex items-center">
                <span className="mr-1">▼</span>
                Click to expand
              </span>
            ) : (
              <span className="inline-flex items-center">
                <span className="mr-1">▲</span>
                Click to collapse
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content area */}
      {!isCollapsed && (
        <div className="collapsible-content">
          {children}
        </div>
      )}
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-current border-t-transparent rounded-full"></div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleBox;